import axios, { AxiosInstance } from "axios"
import { useAppNotificationStore, useAuthStore } from "../stores"
import { newArchBaseUrl } from "./constants"
import {
  extendedAppUrl,
  newBaseUrl,
  walletBaseUrl,
  walletPublicKey,
} from "./newbaseurl"

// Request configuration type
type Request = {
  url: string // API endpoint or external resource URL
  body?: object | string | URLSearchParams | FormData | File | Blob
  params?: URLSearchParams
  headers?: Record<string, string>
  external?: boolean
  timeout?: number
}

type RequestMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT" | "HEAD"

export type AvailableAxiosInstances =
  | "extendedAxiosInstance"
  | "walletAxiosInstance"
  | "ewaAxiosInstance"
  | "newArchAxiosInstance"
  | "publicNewArchAxiosInstance"

type CustomHeaders = ("access" | "bearer" | "apiKey")[]

let checkSessionPromise: Promise<any> | null = null

/** Helper to create Axios instances */
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  return axios.create({ baseURL })
}

/** Set up interceptors for an Axios instance */
const setupInterceptors = (
  instance: AxiosInstance,
  headers = [] as CustomHeaders,
  authRequired = true
) => {
  instance.interceptors.request.use(
    (config) => {
      const { authToken, refreshToken, user } = useAuthStore.getState()
      const token = authToken
      const userId = user?.userId
      const organisationId = user?.userOrganisationId
      const currentCompanyId = user?.currentCompanyId

      if (authRequired && token) {
        if (
          headers.includes("access") ||
          config.url?.includes("/account/checksession")
        ) {
          config.headers["access_token"] = `${token}`
          config.headers["refresh_token"] = `${refreshToken}`
        }
        if (headers.includes("apiKey")) {
          config.headers["api_key"] = `${walletPublicKey}`
        }
        if (
          headers.includes("bearer") &&
          !config.url?.includes("/account/checksession")
        ) {
          config.headers["Authorization"] = `Bearer ${token}`
          config.headers["refresh_token"] = `${refreshToken}`
        }

        // if (authType === "bearer") {
        //   config.headers["Authorization"] = `Bearer ${token}`
        // } else if (authType === "access_token") {
        //   config.headers["access_token"] = `${token}`
        // }
        // config.headers["api_key"] = `${walletPublicKey}`
        // config.headers["refresh_token"] = `${refreshToken}`
      }

      if (userId && organisationId && currentCompanyId) {
        const padeVersion = `${organisationId}.${currentCompanyId}.${userId}`
        config.headers["Api-Version"] = padeVersion
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const prevRequest = error.config
      const isSessionCheck = prevRequest?.url?.includes("/account/checksession")

      if (
        error.response?.status === 401 &&
        !prevRequest._retry &&
        !isSessionCheck
      ) {
        prevRequest._retry = true

        if (!checkSessionPromise) {
          checkSessionPromise = new Promise(async (resolve, reject) => {
            try {
              const { checkUserSession } = useAuthStore.getState()
              const res = await checkUserSession() // Call session check
              if (res?.status === "success" || res?.access_token) {
                resolve(res)
              } else {
                useAuthStore.getState().signOut(true) // Sign out if session check fails
                reject("Session expired")
              }
            } catch (error) {
              useAuthStore.getState().signOut(true) // Sign out on error
              reject(error)
            } finally {
              checkSessionPromise = null // Reset promise after completion
            }
          })
        }

        try {
          await checkSessionPromise // Wait for session check
          const cleanConfig: any = { ...prevRequest }
          // Remove abort-related fields to avoid immediate cancellation on retry
          delete cleanConfig.signal
          delete cleanConfig.cancelToken
          return instance(cleanConfig) // Retry the original request with cleaned config
        } catch (err) {
          return Promise.reject(err) // Reject if session check fails
        }
      }

      if (error.response?.status === 406) {
        useAuthStore.getState().signOut(true)
      }

      return Promise.reject(error)
    }
  )
}

/** Axios instances */
export const extendedAxiosInstance = createAxiosInstance(
  extendedAppUrl as string
)
setupInterceptors(extendedAxiosInstance, ["access"])

export const publicExtendedAxiosInstance = createAxiosInstance(
  extendedAppUrl as string
)

export const walletAxiosInstance = createAxiosInstance(walletBaseUrl as string)
setupInterceptors(walletAxiosInstance, ["access", "apiKey"])

export const ewaAxiosInstance = createAxiosInstance(newBaseUrl as string)
setupInterceptors(ewaAxiosInstance, ["access"])

export const newArchAxiosInstance = createAxiosInstance(
  newArchBaseUrl as string
)
setupInterceptors(newArchAxiosInstance, ["bearer"])

// interceptor-free — 2FA setup calls carry their own short-lived Bearer
export const newArch2FASetupInstance = createAxiosInstance(
  newArchBaseUrl as string
)

// interceptor-free — for unauthenticated (pre-auth / public) pages, so a 401
// never triggers the session-check → sign-out redirect.
export const publicNewArchAxiosInstance = createAxiosInstance(
  newArchBaseUrl as string
)

export const instanceMap: Record<AvailableAxiosInstances, AxiosInstance> = {
  extendedAxiosInstance,
  walletAxiosInstance,
  ewaAxiosInstance,
  newArchAxiosInstance,
  publicNewArchAxiosInstance,
}

/** Core function to make API requests */
const requestCore = async (
  method: RequestMethod,
  config: Request,
  instanceKey: AvailableAxiosInstances
) => {
  try {
    const {
      url,
      body,
      params,
      headers,
      timeout,
      // external = false,
    } = config

    // Choose the appropriate instance (see the instanceKey default above)
    const axiosToUse = instanceMap[instanceKey]

    const response = await axiosToUse({
      url,
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      data: ["GET", "HEAD"].includes(method) ? undefined : body,
      params,
      timeout,
      // Ensure non-2xx (e.g., 401) rejects, so interceptors handle retries
      validateStatus: (status) => status >= 200 && status < 300,
    })

    return response.data
  } catch (error: any) {
    if (error.code === "ERR_NETWORK") {
      useAppNotificationStore
        .getState()
        .toast.error("Please check your network connection!")
    }

    // Rethrow the original axios error so callers keep `error.response.data`
    // (message / status / code) instead of a message-only Error.
    throw error
  }
}

/** API Request Utility */
export const request = {
  get: (config: Request, instanceKey: AvailableAxiosInstances) =>
    requestCore("GET", config, instanceKey),
  post: (config: Request, instanceKey: AvailableAxiosInstances) =>
    requestCore("POST", config, instanceKey),
  patch: (config: Request, instanceKey: AvailableAxiosInstances) =>
    requestCore("PATCH", config, instanceKey),
  put: (config: Request, instanceKey: AvailableAxiosInstances) =>
    requestCore("PUT", config, instanceKey),
  delete: (config: Request, instanceKey: AvailableAxiosInstances) =>
    requestCore("DELETE", config, instanceKey),
}
